import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

async function download(url, name) {
  const { data } = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = name; a.click();
}

const TONE = { new: "success", update: "info", duplicate: "warning", invalid: "danger" };

export function AccountImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [upd, setUpd] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setRes(null); setUpd(false); };
  const validate = async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("update_existing", upd);
    setBusy(true);
    try { const { data } = await api.post("/accounts/import/validate", fd); setRes(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/accounts/import/${res.batch.id}/commit`); toast.success(`${data.created} created · ${data.updated} updated`); reset(); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const c = res?.batch.counts;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="account-import-dialog">
        <DialogHeader><DialogTitle>Import subscriber accounts</DialogTitle>
          <DialogDescription>Columns: account number, account name (required), consumer category, consumer type, service address, barangay, municipality. Category and type must match Admin Settings.</DialogDescription></DialogHeader>
        {!res ? (
          <div className="space-y-3">
            <Button size="sm" variant="outline" onClick={() => download("/accounts/import/template.csv", "subscriber-accounts-template.csv")} data-testid="account-template-download">Download CSV template</Button>
            <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0] || null)} className="block text-sm" data-testid="account-import-file" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={upd} onChange={(e) => setUpd(e.target.checked)} data-testid="account-import-update" />Update existing accounts with the same account number</label>
            <DialogFooter><Button disabled={!file || busy} onClick={validate} data-testid="account-import-validate">Validate file</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm" data-testid="account-import-summary">
              <StatusBadge tone="success">{c.new} new</StatusBadge><StatusBadge tone="info">{c.update} update</StatusBadge>
              <StatusBadge tone="warning">{c.duplicate} duplicate (skipped)</StatusBadge><StatusBadge tone="danger">{c.invalid} invalid</StatusBadge>
            </div>
            <div className="overflow-x-auto max-h-80"><table className="w-full text-xs">
              <thead className="uppercase text-slate-500"><tr className="text-left"><th className="p-2">Row</th><th>Account no.</th><th>Name</th><th>Category / type</th><th>Result</th></tr></thead>
              <tbody>{res.preview.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`import-row-${r.row_number}`}><td className="p-2">{r.row_number}</td><td className="font-mono">{r.account_number}</td><td>{r.account_name}</td>
                  <td>{[r.consumer_category, r.consumer_type].filter(Boolean).join(" · ") || "—"}</td>
                  <td><StatusBadge tone={TONE[r.result]}>{r.result}</StatusBadge>{r.errors.length > 0 && <p className="text-rose-700 mt-1">{r.errors.join("; ")}</p>}</td></tr>
              ))}</tbody>
            </table></div>
            <DialogFooter className="gap-2">
              {(c.invalid + c.duplicate) > 0 && <Button variant="outline" onClick={() => download(`/accounts/import/${res.batch.id}/errors.csv`, "account-import-errors.csv")} data-testid="account-import-errors">Download error report</Button>}
              <Button variant="outline" onClick={reset} data-testid="account-import-restart">Choose another file</Button>
              <Button disabled={busy || c.new + c.update === 0} onClick={commit} data-testid="account-import-commit">Save {c.new + c.update} account(s)</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
